import React, { useState, useEffect } from 'react';
import {
  Modal,
  Backdrop,
  Fade,
  CircularProgress,
  IconButton,
  TextField,
  Box,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { globalConfig } from '../configuration/config';
import { useTranslation } from 'react-i18next';

const RetryByPhotoModal = ({ onCloseMenu, open, setOpen, projectGuid, messageGuid, messageData }) => {
  const { t } = useTranslation();
  // Модалка должна открываться, когда компонент смонтирован родителем
  // Управление закрытием передаём наверх, чтобы сбрасывать флаг рендера
  const [localOpen, setLocalOpen] = useState(true);
  const isControlled = open !== undefined && setOpen !== undefined;
  const isModalOpen = isControlled ? open : localOpen;
  const setIsModalOpen = isControlled ? setOpen : setLocalOpen;

  const [widthMm, setWidthMm] = useState('');
  const [heightMm, setHeightMm] = useState('');
  const [depthMm, setDepthMm] = useState('');
  const [widthError, setWidthError] = useState('');
  const [heightError, setHeightError] = useState('');
  const [depthError, setDepthError] = useState('');
  const [uploadMessage, setUploadMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isUploadComplete, setIsUploadComplete] = useState(false);

  // Автоматическое заполнение полей размеров из данных сообщения
  useEffect(() => {
    console.log('RetryByPhotoModal получил messageData:', messageData);
    
    if (messageData) {
      // Проверяем поля размеров originX, originY, originZ
      const originX = messageData.originX;
      const originY = messageData.originY;
      const originZ = messageData.originZ;
      
      console.log('Поля размеров в сообщении:', { originX, originY, originZ });
      
      // Заполняем поля только если они не null, не undefined и не пустые
      if (originX !== null && originX !== undefined && originX !== '') {
        setWidthMm(originX.toString());
        console.log('Заполнено поле ширина:', originX);
      }
      if (originY !== null && originY !== undefined && originY !== '') {
        setHeightMm(originY.toString());
        console.log('Заполнено поле высота:', originY);
      }
      if (originZ !== null && originZ !== undefined && originZ !== '') {
        setDepthMm(originZ.toString());
        console.log('Заполнено поле глубина:', originZ);
      }
      
      // Если все поля пустые, выводим сообщение
      if ((originX === null || originX === undefined || originX === '') &&
          (originY === null || originY === undefined || originY === '') &&
          (originZ === null || originZ === undefined || originZ === '')) {
        console.log('Все поля размеров пустые, форма остается пустой');
      }
    }
  }, [messageData]);

  // Блокировка нецелых/отрицательных значений в числовых полях
  const blockNonNumericKeys = (event) => {
    const blockedKeys = ['e', 'E', '+', '-', '.', ',', ' '];
    if (blockedKeys.includes(event.key)) {
      event.preventDefault();
    }
  };

  const handleIntChange = (setter) => (event) => {
    const digitsOnly = event.target.value.replace(/[^0-9]/g, '');
    setter(digitsOnly);
  };

  const handlePasteNumericOnly = (event) => {
    const text = (event.clipboardData || window.clipboardData).getData('text');
    if (!/^\d+$/.test(text)) {
      event.preventDefault();
    }
  };

  const handleOpen = () => setIsModalOpen(true);
  const handleClose = () => {
    setIsModalOpen(false);
    resetState();
    // Важно всегда уведомлять родителя, чтобы он снял флаг рендера модалки
    if (typeof onCloseMenu === 'function') onCloseMenu();
  };

  const resetState = () => {
    setWidthMm('');
    setHeightMm('');
    setDepthMm('');
    setWidthError('');
    setHeightError('');
    setDepthError('');
    setUploadMessage('');
    setLoading(false);
    setIsUploadComplete(false);
  };

  const uploadFiles = async () => {
    setUploadMessage('');

    // Валидация размеров
    const w = widthMm !== '' ? parseInt(widthMm, 10) : null;
    const h = heightMm !== '' ? parseInt(heightMm, 10) : null;
    const d = depthMm !== '' ? parseInt(depthMm, 10) : null;

    let hasError = false;
    if (w !== null && (Number.isNaN(w) || w <= 0)) {
      setWidthError(t('retryByPhoto.errors.mustBeGreaterThanZero'));
      hasError = true;
    } else {
      setWidthError('');
    }
    if (h !== null && (Number.isNaN(h) || h <= 0)) {
      setHeightError(t('retryByPhoto.errors.mustBeGreaterThanZero'));
      hasError = true;
    } else {
      setHeightError('');
    }
    if (d !== null && (Number.isNaN(d) || d <= 0)) {
      setDepthError(t('retryByPhoto.errors.mustBeGreaterThanZero'));
      hasError = true;
    } else {
      setDepthError('');
    }

    if (hasError) {
      setUploadMessage(t('retryByPhoto.messages.checkValues'));
      return;
    }

    // Проверяем, что хотя бы одно поле заполнено
    if (w === null && h === null && d === null) {
      setUploadMessage(t('retryByPhoto.messages.fillAtLeastOne'));
      return;
    }

    const requestData = {
      MessageGuid: messageGuid,
      Width: w || 0,
      Height: h || 0,
      Depth: d || 0,
    };

    setLoading(true);
    setIsUploadComplete(false);

    try {
      await axios.post(`${globalConfig.config.common.api}Projects/RetryByPhotoConvert`, requestData, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true,
      });

      setUploadMessage(t('retryByPhoto.messages.sentSuccess'));
      setIsUploadComplete(true);
    } catch (error) {
      console.error('RetryByPhoto send error:', error);
      setUploadMessage(t('retryByPhoto.messages.sendError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={isModalOpen}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}>
        <Fade in={isModalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              boxShadow: 5,
              padding: 3,
              borderRadius: 2,
              width: '90vw',
              maxWidth: '500px',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}>
            <IconButton
              onClick={handleClose}
              sx={{ position: 'absolute', top: 8, right: 8, color: 'grey.500', zIndex: 1 }}>
              <CloseIcon />
            </IconButton>

            <Box sx={{ flex: '1 1 auto', overflowY: 'auto', pr: 1, pb: 1 }}>
              <Typography variant="h6" sx={{ marginBottom: 2, fontSize: '18px', fontWeight: '500' }}>
                {t('retryByPhoto.title')}
              </Typography>

              <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 400, marginBottom: 2, color: '#666' }}>
                {t('retryByPhoto.subtitle')}
              </Typography>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 2,
                alignItems: 'start',
                marginBottom: 2
              }}>
                <TextField
                  label={t('retryByPhoto.labels.width')}
                  type="number"
                  value={widthMm}
                  onChange={handleIntChange(setWidthMm)}
                  error={!!widthError}
                  helperText={widthError}
                  inputProps={{ min: 1, step: 1, inputMode: 'numeric', pattern: '\\d*' }}
                  onKeyDown={blockNonNumericKeys}
                  onPaste={handlePasteNumericOnly}
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                />
                <TextField
                  label={t('retryByPhoto.labels.height')}
                  type="number"
                  value={heightMm}
                  onChange={handleIntChange(setHeightMm)}
                  error={!!heightError}
                  helperText={heightError}
                  inputProps={{ min: 1, step: 1, inputMode: 'numeric', pattern: '\\d*' }}
                  onKeyDown={blockNonNumericKeys}
                  onPaste={handlePasteNumericOnly}
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                />
                <TextField
                  label={t('retryByPhoto.labels.depth')}
                  type="number"
                  value={depthMm}
                  onChange={handleIntChange(setDepthMm)}
                  error={!!depthError}
                  helperText={depthError}
                  inputProps={{ min: 1, step: 1, inputMode: 'numeric', pattern: '\\d*' }}
                  onKeyDown={blockNonNumericKeys}
                  onPaste={handlePasteNumericOnly}
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={{ '& .MuiInputBase-root': { height: '40px' } }}
                />
              </Box>

              {uploadMessage && (
                <Box
                  sx={{
                    marginTop: 2,
                    p: 2,
                    backgroundColor: (uploadMessage.includes('успешно') || uploadMessage.includes('success')) ? '#e6f4ea' : '#fcebea',
                    borderRadius: 2,
                    fontSize: 12,
                    color: (uploadMessage.includes('успешно') || uploadMessage.includes('success')) ? '#2e7d32' : '#cc0000',
                  }}>
                  {uploadMessage}
                </Box>
              )}

              {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', marginTop: 2 }}>
                  <CircularProgress size={16} />
                  <Typography sx={{ ml: 1, fontSize: '14px' }}>{t('retryByPhoto.sending')}</Typography>
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: 'flex',
                padding: '8px 16px',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                background: '#FFF',
                borderTop: '1px solid #eee',
                height: '60px',
                flexShrink: 0
              }}>
              <button
                onClick={isUploadComplete ? handleClose : uploadFiles}
                disabled={loading}
                style={{
                  width: '100%',
                  borderRadius: '8px',
                  background: '#E0E0E0',
                  color: '#232323',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  padding: '8px 16px',
                  border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}>
                {isUploadComplete ? 'OK' : t('retryByPhoto.sendButton')}
              </button>
            </Box>
          </Box>
        </Fade>
      </Modal>

      {!isControlled && (
        <div className="bm-item menu-item" onClick={handleOpen}>
          {t('retryByPhoto.title')}
        </div>
      )}
    </>
  );
};

export default RetryByPhotoModal;
